import { useState } from "react";
import {
  Button,
  CalendarPopover,
  DateValue,
  Icon,
  parseDate,
  Tag,
  Text,
} from "@ttc3k/trekker";
import { Calendar, NavArrowDown, Xmark } from "iconoir-react";
import { Box, Flex, styled } from "styled-system/jsx";
import { SelectableRadioCard } from "shared/components";
import { parseCalendarValueAsString } from "shared/utils";
import { DAY_CARD_THRESHOLD } from "./useEventPurchaseFlow";

export const formatDayLabel = (dateISO: string, language: string): string =>
  new Date(`${dateISO}T00:00:00`).toLocaleDateString(language, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const toISO = (date: DateValue): string =>
  `${date.year}-${String(date.month).padStart(2, "0")}-${String(
    date.day,
  ).padStart(2, "0")}`;

type EventDaySelectorProps = {
  days: string[];
  value: string[];
  onChange: (next: string[]) => void;
  multiple?: boolean;
  maxCount?: number;
  isDaySoldOut: (dateISO: string) => boolean;
  isToday?: (dateISO: string) => boolean;
  isTomorrow?: (dateISO: string) => boolean;
  todayLabel?: string;
  tomorrowLabel?: string;
  language: string;
  /** Show removable chips for the current selection (multi only). */
  showChips?: boolean;
  /** Calendar trigger text when nothing is selected yet. */
  placeholder?: string;
};

export const EventDaySelector = ({
  days,
  value,
  onChange,
  multiple = false,
  maxCount,
  isDaySoldOut,
  isToday,
  isTomorrow,
  todayLabel = "Today",
  tomorrowLabel = "Tomorrow",
  language,
  showChips = false,
  placeholder,
}: EventDaySelectorProps) => {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const daySet = new Set(days);
  const atCap = maxCount != null && value.length >= maxCount;

  const pickDay = (dateISO: string) => {
    if (!multiple) {
      onChange([dateISO]);
      return;
    }
    if (value.includes(dateISO)) {
      onChange(value.filter(d => d !== dateISO));
      return;
    }
    // A 1-pick "multi" behaves like single select: replace freely.
    if (maxCount === 1) {
      onChange([dateISO]);
      return;
    }
    if (atCap) return;
    onChange([...value, dateISO]);
  };

  // Other valid days are only locked once the cap is reached on a true
  // multi-pick (maxCount > 1); a 1-pick can always be changed.
  const capLocks = atCap && maxCount !== 1;

  const useCalendar = days.length >= DAY_CARD_THRESHOLD;
  const selectedLabel =
    !multiple && value[0] ? formatDayLabel(value[0], language) : null;
  const triggerPlaceholder =
    placeholder ?? (multiple ? "Choose your dates" : "Choose your date");

  return (
    <Flex flexDirection="column" gap="150">
      {useCalendar ? (
        <Box position="relative">
          {calendarOpen && (
            <Box
              position="fixed"
              inset="0"
              zIndex="10"
              onClick={() => setCalendarOpen(false)}
            />
          )}
          <CalendarPopover
            selectionMode="single"
            value={
              !multiple && value[0] ? [parseDate(value[0])] : []
            }
            closeOnSelect={!multiple}
            open={calendarOpen}
            onOpenChange={({ open }: { open: boolean }) =>
              setCalendarOpen(open)
            }
            min={days[0] ? parseDate(days[0]) : undefined}
            max={days[days.length - 1] ? parseDate(days[days.length - 1]) : undefined}
            isDateUnavailable={(date: DateValue) => {
              const iso = toISO(date);
              if (!daySet.has(iso)) return true;
              if (isDaySoldOut(iso)) return true;
              if (multiple && capLocks && !value.includes(iso)) return true;
              return false;
            }}
            onValueChange={({ valueAsString }: { valueAsString: string[] }) => {
              const picked = parseCalendarValueAsString(valueAsString)[0];
              if (picked) pickDay(picked);
              if (!multiple) setCalendarOpen(false);
            }}
            outline="none"
            noPortal
            width="full"
            zIndex={calendarOpen ? 11 : 0}
            Trigger={
              <Button
                visual="primary"
                mode="light"
                type="button"
                width="full"
                height="auto"
                minHeight="48px"
                py="150"
                px="200"
                gap="150"
                justifyContent="flex-start"
                textAlign="left"
                aria-expanded={calendarOpen}
                onClick={() => setCalendarOpen(prev => !prev)}
              >
                <Icon
                  Element={Calendar}
                  color={selectedLabel ? "icon.accent.blue.mid" : "icon.mid"}
                  width="20px"
                  height="20px"
                  flexShrink={0}
                />
                <Text
                  visual={selectedLabel ? "bodySemiBold" : "bodyMedium"}
                  color={selectedLabel ? "text.dark" : "text.mid"}
                  flexGrow={1}
                  textAlign="left"
                >
                  {selectedLabel ?? triggerPlaceholder}
                </Text>
                <Icon
                  Element={NavArrowDown}
                  color="icon.light"
                  width="16px"
                  height="16px"
                  flexShrink={0}
                  transform={calendarOpen ? "rotate(180deg)" : undefined}
                  transition="transform 0.15s ease"
                />
              </Button>
            }
          />
        </Box>
      ) : (
        <Flex flexDirection="column" gap="100">
          {days.map(dateISO => {
            const soldOut = isDaySoldOut(dateISO);
            const selected = value.includes(dateISO);
            const dayLabel = formatDayLabel(dateISO, language);
            const suffix = isToday?.(dateISO)
              ? todayLabel
              : isTomorrow?.(dateISO)
                ? tomorrowLabel
                : null;
            const label = suffix ? `${dayLabel} — ${suffix}` : dayLabel;
            return (
              <SelectableRadioCard
                key={dateISO}
                selectedValue={selected ? dateISO : ""}
                radioValue={dateISO}
                onSelectRadio={() => pickDay(dateISO)}
                title={label}
                selectionIndicator={
                  multiple && maxCount !== 1 ? "checkbox" : "none"
                }
                disabled={soldOut || (multiple && capLocks && !selected)}
              />
            );
          })}
        </Flex>
      )}

      {showChips && multiple && useCalendar && value.length > 0 && (
        <Flex gap="100" flexWrap="wrap">
          {value.map(dateISO => (
            <Tag key={dateISO} visual="highlight" mode="light">
              <Flex alignItems="center" gap="50">
                {formatDayLabel(dateISO, language)}
                <styled.button
                  type="button"
                  cursor="pointer"
                  display="inline-flex"
                  onClick={() => pickDay(dateISO)}
                >
                  <Icon Element={Xmark} width="14px" height="14px" />
                </styled.button>
              </Flex>
            </Tag>
          ))}
        </Flex>
      )}
    </Flex>
  );
};
